import { handleUnauthorized } from '@utils/apiHelpers'

const SESSION_KEY = 'auth.profileTimezoneSync'

type SyncRecord = {
  userId: string
  timezone: string
}

let inFlight: Promise<void> | null = null

function readSyncRecord(): SyncRecord | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SyncRecord
    if (!parsed || typeof parsed.timezone !== 'string' || typeof parsed.userId !== 'string') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeSyncRecord(record: SyncRecord): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(record))
  } catch {
    /* ignore quota / private mode */
  }
}

function readBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

export function resetProfileTimezoneSync(): void {
  inFlight = null
}

/** PATCH /api/settings/profile with the browser timezone, once per browser session. */
export function syncBrowserTimezoneOnce(
  apiBaseUrl: string,
  accessToken: string | null,
  authHeader: Record<string, string>,
  userId?: number | null,
): void {
  if (!accessToken || !apiBaseUrl) return
  if (inFlight) return

  const timezone = readBrowserTimezone()
  if (!timezone) return

  const identity = userId != null && Number.isFinite(userId) ? String(userId) : accessToken
  const prev = readSyncRecord()
  if (prev && prev.userId === identity && prev.timezone === timezone) return

  const base = apiBaseUrl.replace(/\/$/, '')

  inFlight = fetch(`${base}/api/settings/profile`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
    },
    body: JSON.stringify({ timezone }),
  })
    .then((res) => {
      if (handleUnauthorized(res)) return
      if (res.ok) writeSyncRecord({ userId: identity, timezone })
    })
    .catch(() => {
      /* retry on the next authenticated mount */
    })
    .finally(() => {
      inFlight = null
    })
}
